<template>
  <div class="educational-material-box">
    <!-- Educational Materials View -->
    <educational-material-list
      v-if="showEducationalMaterials"
      ref="educationalMaterialList"
      :current-folder="currentFolder"
      :tumor-sites="tumorSites"
      @folder-changed="$emit('folder-changed')"
      @open-type-selector="openTypeSelector"
      @edit-item="editItem"
      @share-button-click="onShareButtonClick"
      @move-to-public="handleMoveToPublic"
    />

    <!-- Assigned Users View -->
    <assigned-users-list
      v-else-if="showAssignedUsers"
      ref="assignedUsersList"
      :current-folder="currentFolder"
      @folder-changed="$emit('folder-changed')"
      @view-responses="handleViewResponses"
      @edit-assignment="handleEditAssignment"
    />

    <!-- Responses View -->
    <responses-from-recipients
      v-else-if="showResponsesFromRecipients"
      ref="responsesFromRecipients"
      :current-folder="currentFolder"
      @folder-changed="$emit('folder-changed')"
    />

    <!-- Shared Modals and Components -->
    <educational-material-add
      :edit-id="selectedEditId"
      :type="selectedType"
      :selected-user-id="selectedUserId"
      :current-folder="currentFolder"
      :tumor-sites="tumorSites"
      @saved="handleMaterialSaved"
    />

    <edu-material-copy
      :system-id-number="systemIdNumber"
      :creation-id="creationId"
      :show="showCopyModal"
      @closed="handleCopyModalClosed"
    />

    <!-- Create Type Modal -->
    <b-modal
      id="create-type-modal"
      title="Create Educational Material"
      hide-footer
    >
      <div class="d-flex flex-column align-items-center">
        <b-button
          block
          variant="primary"
          class="mb-2 btn-primary px-4 rounded-pill"
          @click="proceedToForm('Collection')"
        >
          Tumor Case Collection
        </b-button>

        <b-button
          block
          variant="primary"
          class="mb-2 btn-primary px-4 rounded-pill"
          @click="proceedToForm('Assignment')"
        >
          Assignment
        </b-button>
      </div>

      <div class="text-right mt-3">
        <b-button
          variant="secondary"
          @click="$bvModal.hide('create-type-modal')"
        >
          Cancel
        </b-button>
      </div>
    </b-modal>

    <!-- Share Popover Container -->
    <div class="share-popover-container" style="position: fixed; z-index: 1001">
      <div
        class="edit-case-menu shadow-toolbar"
        v-if="sharePopover.show"
        :style="{
          top: sharePopover.y,
          left: sharePopover.x,
          position: 'fixed',
          display: 'block',
          width: '200px',
          height:
            sharePopover.material.type === 'Assignment' ? '140px' : '100px',
        }"
        @click.stop
      >
        <div class="popover-content text-center position-relative">
          <div class="toolbar-close-icon" @click="closeSharePopover">
            <svg
              id="Icon_ionic-md-close-circle-outline"
              data-name="Icon ionic-md-close-circle-outline"
              xmlns="http://www.w3.org/2000/svg"
              width="16.062"
              height="16.062"
              viewBox="0 0 16.062 16.062"
            >
              <path
                id="Path_10189"
                data-name="Path 10189"
                d="M11.406,5A6.406,6.406,0,1,1,6.873,6.873,6.383,6.383,0,0,1,11.406,5m0-1.622a8.031,8.031,0,1,0,8.031,8.031,8.03,8.03,0,0,0-8.031-8.031Z"
                transform="translate(-3.375 -3.375)"
                fill="#204c90"
              />
              <path
                id="Path_10190"
                data-name="Path 10190"
                d="M18.718,17.595l-1.124,1.124L14.7,15.826l-2.892,2.892-1.124-1.124L13.579,14.7l-2.892-2.892,1.124-1.124L14.7,13.579l2.892-2.892,1.124,1.124L15.826,14.7Z"
                transform="translate(-6.672 -6.672)"
                fill="#204c90"
              />
            </svg>
          </div>
          <div class="d-flex flex-column">
            <b-btn
              class="m-1 text-nowrap btn-rounded"
              variant="primary"
              size="sm"
              @click="shareMaterial('qr')"
            >
              Generate QR code
            </b-btn>
            <b-btn
              class="m-1 text-nowrap btn-rounded"
              variant="primary"
              size="sm"
              @click="shareMaterial('link')"
            >
              Copy link
            </b-btn>
            <b-btn
              class="m-1 text-nowrap btn-rounded"
              variant="primary"
              size="sm"
              @click="(event) => openAssignUserModal(event)"
              v-if="sharePopover.material.type === 'Assignment'"
            >
              Assign To Users
            </b-btn>
          </div>
        </div>
      </div>
    </div>

    <!-- Assign Popover -->
    <div
      v-if="assignPopover.show"
      class="share-popover-container edit-case-menu assign-popover"
      :style="{
        position: 'fixed',
        top: assignPopover.y,
        left: assignPopover.x,
        zIndex: 1002,
      }"
    >
      <assign-to-user-form
        :assignment="assignPopover.assignment"
        @assign="handleAssignToUsers"
        @resend-existing="handleResendToExistingUsers"
        @close="assignPopover.show = false"
      />
    </div>

    <!-- Hidden canvas for QR code -->
    <canvas ref="qrCanvas" style="display: none"></canvas>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import * as folderTypes from "../../../constants/constants-folder";
import * as roleTypes from "../../../constants/constants-role";
import QRCode from "qrcode";
import _ from "lodash";

// Import child components
import EducationalMaterialList from "./educational-materials/EducationalMaterialList.vue";
import AssignedUsersList from "./educational-materials/AssignedUsersList.vue";
import ResponsesFromRecipients from "./educational-materials/ResponsesFromRecipients.vue";
import EducationalMaterialAdd from "../components/add-educational-material.vue";
import AssignToUserForm from "../components/assign-to-user-form.vue";
import EduMaterialCopy from "../../../components/common/edu-material-copy.vue";

export default {
  name: "EducationalMaterialBox",
  data() {
    // const userRole = localStorage.getItem("userRole")?.toUpperCase();
    // const isTenantAdmin = userRole === "TENANT ADMIN";
    return {
      ...folderTypes,
      ...roleTypes,
      tumorSites: [],
      userDetail: null,
      tumorSiteId: null,
      selectedType: null,
      selectedEditId: null,
      selectedUserId: null,
      systemIdNumber: null,
      creationId: null,
      showCopyModal: false,
      sharePopover: {
        material: null,
        show: false,
        x: "",
        y: "",
      },
      assignPopover: {
        show: false,
        x: "",
        y: "",
        assignment: null,
      },
    };
  },
  components: {
    EducationalMaterialList,
    AssignedUsersList,
    ResponsesFromRecipients,
    EducationalMaterialAdd,
    AssignToUserForm,
    EduMaterialCopy,
  },
  props: {
    currentFolder: {
      type: Object,
      default: null,
    },
  },
  computed: {
    showEducationalMaterials() {
      return (
        this.currentFolder &&
        this.currentFolder.folderType !== "ASSIGNEDTOUSERS" &&
        this.currentFolder.folderType !== "RESPONSEFROMRECIPIENTSCHILD"
      );
    },
    showAssignedUsers() {
      return (
        this.currentFolder &&
        this.currentFolder.folderType === "ASSIGNEDTOUSERS"
      );
    },
    showResponsesFromRecipients() {
      return (
        this.currentFolder &&
        this.currentFolder.folderType === "RESPONSEFROMRECIPIENTSCHILD"
      );
    },
  },
  watch: {
    currentFolder: {
      handler(newFolder) {
        if (newFolder) {
          console.log("Current folder changed:", newFolder);
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    window.addEventListener("click", this.closeSharePopover);
    await this.initializeData();
  },
  beforeDestroy() {
    window.removeEventListener("click", this.closeSharePopover);
  },
  methods: {
    ...mapActions(["setSelectedCaseSlideIds"]),

    async fetchFolderMaterials() {
      await this.$nextTick();

      if (this.showEducationalMaterials && this.$refs.educationalMaterialList) {
        return this.$refs.educationalMaterialList.fetchFolderMaterials();
      }

      if (this.showAssignedUsers && this.$refs.assignedUsersList) {
        return this.$refs.assignedUsersList.fetchAssignedMaterials();
      }

      if (
        this.showResponsesFromRecipients &&
        this.$refs.responsesFromRecipients
      ) {
        return this.$refs.responsesFromRecipients.fetchRecipientsList();
      }
    },

    async initializeData() {
      try {
        const storedTumorSites = localStorage.getItem('tumorSites');
        if (storedTumorSites) {
          this.tumorSites = JSON.parse(storedTumorSites);
        } else {
          await this.getUserDetails();
        }

        this.initialLoading = false;

        if (this.currentFolder) {
          await this.fetchFolderMaterials(this.currentFolder);
        }
      } catch (error) {
        console.error("Error initializing data:", error);
        this.$handleError(error);
      }
    },

    async getUserDetails() {
      try {
        const response = await this.$api.getUserDetailFact(
          localStorage.getItem("userId")
        );
        this.userDetail = response.data;

        if (this.userDetail?.userTumorSites) {
          this.tumorSiteId = this.userDetail.userTumorSites.id;
          await this.getAllMetadataList(this.tumorSiteId);
        } else {
          this.tumorSiteId = null;
        }

        return this.userDetail;
      } catch (error) {
        this.$handleError(error);
        throw error;
      }
    },

    async getAllMetadataList(tumorSiteId) {
      try {
        const response = await this.$api.getAllMetadataListFact(tumorSiteId);
        const allDataList = response.data.payload;
        if (allDataList != null) {
          this.tumorSites = allDataList.tumorSites;
        }
        return allDataList;
      } catch (error) {
        this.$handleError(error);
        throw error;
      }
    },

    handleGridModeChange(mode) {
      this.gridMode = mode;
    },
    handleSizeChange(params) {
      this.paginationParams.size = params.size;
      this.paginationParams.page = 0;
      if (this.currentFolder && !this.initialLoading) {
        this.fetchFolderMaterials(this.currentFolder);
      }
    },
    handlePageChange(params) {
      this.paginationParams.page = params.page;
      if (this.currentFolder && !this.initialLoading) {
        this.fetchFolderMaterials(this.currentFolder);
      }
    },
    handleFilterChange(newFilters) {
      const userRole = localStorage.getItem("userRole")?.toUpperCase();
    const isTenantAdmin = userRole === "TENANT ADMIN";
      if (Object.keys(newFilters).length === 0) {
    this.currentFilters = {
      search: "",
      slideCollectionType: "",
      level: "",
      tumorSiteIdsList: isTenantAdmin
        ? localStorage.getItem("tumorSiteIdsString") || null
        : localStorage.getItem("tumorSiteId"),
      isRecommended: null,
    };
  } else {
    this.currentFilters = {
      ...this.currentFilters,
      ...newFilters,
    };
  }
      
      this.paginationParams.page = 0;
      if (this.currentFolder && !this.initialLoading) {
        this.fetchFolderMaterials(this.currentFolder);
      }
    },

    async viewMaterialInfo(eduMaterial) {
      var query;
      var isPublicSite = this.currentFolder.folderType === "EDUCATIONALPUBLIC" ? true : false;
      const route = "edu-case-viewer";
      this.$changeRouteToTab({
        name: route,
        params: {
          id: eduMaterial.id,
          isPublicSite,
          tumorSiteId: eduMaterial.tumorSiteId,
          tumorSiteName: eduMaterial.tumorSite,
          type: eduMaterial.type,
          backToPublicSite: false,
          activeTab: 'none',
          isMaterialPublished: eduMaterial.isShowPublicSite
        },
        query: query ? query.query : null,
      });
    },
    openTypeSelector() {
      this.$bvModal.show("create-type-modal");
    },

    proceedToForm(type) {
      this.selectedType = type;
      this.selectedEditId = null;
      this.selectedUserId = null;
      this.$bvModal.hide("create-type-modal");
      this.$bvModal.show("create-material-modal");
    },

    handleMaterialSaved() {
      this.$bvModal.hide("create-material-modal");
      this.$emit("material-saved");
    },

    handleCopyModalClosed() {
      this.showCopyModal = false;
      this.selectedMaterial = null;
      this.systemIdNumber = null;
      this.creationId = null;
    },

    editItem(item) {
      this.selectedEditId = item.id;
      this.selectedType = item.type;
      this.selectedUserId = item.author;
      this.$bvModal.show("create-material-modal");
    },

    handleMoveToPublic(data) {
      this.systemIdNumber = data.systemIdNumber;
      this.creationId = data.creationId;
      this.showCopyModal = true;
    },

    closeSharePopover() {
      this.sharePopover = {
        material: null,
        show: false,
        x: "",
        y: "",
      };
    },

    onShareButtonClick(event, material) {
      if (!event) {
        console.error("Event is undefined!");
        return;
      }
      this.selectedMaterial = material;

      if (this.$userAgent.isMobile) {
        this.sharePopover = {
          material: material,
          show: true,
          x: "10px",
          y: "10px",
        };
      } else {
        const rect = event.getBoundingClientRect();
        const popoverWidth = 200;
        const x = rect.right - popoverWidth;
        const y = rect.bottom + 5;

        console.log("Positioning popover at:", { x, y, rect });

        this.sharePopover = {
          material: material,
          show: true,
          x: `${x}px`,
          y: `${y}px`,
        };
      }
    },

    async shareMaterial(type) {
      this.closeSharePopover();

      if (!this.selectedMaterial) return;
      console.log("Sharing material:", this.selectedMaterial);
      const material = this.selectedMaterial;
      const isPublicSite = true; // Always true for sharing
      const backToPublicSite = false;
      const params = {
        id: material.id,
        isPublicSite: isPublicSite,
        tumorSiteId: localStorage.getItem("tumorSiteId") || 1,
        tumorSiteName: material.tumorSite,
        type: material.type,
        backToPublicSite: backToPublicSite,
        activeTab: "none",
        isMaterialPublished: material.isShowPublicSite
      };

      const encryptedParams = _.mapValues(params, (value) =>
        this.$encryptData(value != null ? value : "")
      );

      const encryptedShareUrl = `${encryptedParams.id}/${encryptedParams.isPublicSite}/${encryptedParams.tumorSiteId}/${encryptedParams.type}/${encryptedParams.backToPublicSite}/${encryptedParams.activeTab}/${encryptedParams.tumorSiteName}/${encryptedParams.isMaterialPublished}`;

      try {
        const response = await this.$api.postCaseUrl({
          encryptedData: encryptedShareUrl,
        });
        if (response && response.data.id) {
          const shortUrl = `${window.location.origin}/edu-short-links/${response.data.id}`;
          if (type === "assigntouser") {
            return { shortUrl, responseId: response.data.id };
          } else if (type === "qr") {
            const canvas = this.$refs.qrCanvas;
            await QRCode.toCanvas(canvas, shortUrl, { width: 300 });
            const link = document.createElement("a");
            link.href = canvas.toDataURL("image/png");
            const fileName = `${
              material.systemIdNumber || material.idNumber
            }.png`;
            link.download = fileName;
            link.click();
          } else {
            navigator.clipboard
              .writeText(shortUrl)
              .then(() => {
                this.$toastr("success", "Copied to clipboard");
              })
              .catch((err) => {
                this.$toastr("error", `Failed to copy - ${err}`);
              });
          }
        }
      } catch (error) {
        console.error("Failed to create short link:", error);
        this.$toastr("error", "Failed to generate share link");
      }
    },

    openAssignUserModal(event) {
      const rect = event.target.getBoundingClientRect();

      this.assignPopover = {
        show: true,
        x: `${rect.left - 150}px`,
        y: `${rect.bottom - 120}px`,
        assignment: null,
      };

      this.sharePopover = {
        material: null,
        show: false,
        x: "",
        y: "",
      };
    },

    async handleAssignToUsers(payload) {
      try {
        if (payload.isEditMode) {
          this.selectedMaterial = {
            id: payload.id,
            type: payload.type,
            tumorSite: payload.tumorSite,
          };
        }
        const result = await this.shareMaterial("assigntouser");

        if (!result) {
          this.$toastr("error", "Share link generation failed");
          return;
        }
        const { shortUrl, responseId } = result;
        console.log("Short URL for assignment:", shortUrl, responseId, payload);
        const response = await this.$api.assignMaterialToUsers({
          materialId: this.selectedMaterial.id,
          deadlineDate: payload.deadline,
          startDate: payload.startDate,
          receivePdfCopy: payload.includePdf,
          recipientEmails: payload.emails,
          shortlink: shortUrl,
          randomId: responseId,
        });
        if (this.$handleResponse && !this.$handleResponse(response)) {
          return false;
        }
        this.$toastr("success", "Assigned successfully!");
        this.$emit("refresh-folders");
      } catch (e) {
        this.$handleError(e);
      }
    },

    async handleResendToExistingUsers(payload) {
      try {
        const response = await this.$api.reassignMaterialToUsers({
          assignmentId: payload.assignmentId,
          materialId: payload.materialId,
          deadlineDate: payload.deadline,
          receivePdfCopy: payload.includePdf,
          resendRecipientEmails: payload.emails,
          randomId: payload.randomId,
        });

        if (this.$handleResponse && !this.$handleResponse(response)) {
          return false;
        }
        this.$toastr("success", "Resent successfully!");
      } catch (e) {
        this.$handleError(e);
      }
    },

    handleEditAssignment(payload) {
      const { event, assignment } = payload;
      const rect = event.getBoundingClientRect();

      this.assignPopover = {
        show: true,
        x: `${rect.left - 150}px`,
        y: `${rect.bottom}px`,
        assignment,
      };
    },

    handleViewResponses(payload) {
      this.$emit("view-responses", payload);
    },
  },
};
</script>

<style lang="scss" scoped>
.share-popover-container .edit-case-menu {
  position: absolute;
}
.assign-popover {
  padding: 12px 16px;
}
</style>
